import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { Instance } from "mobx-state-tree";

import WorkoutStore from "stores/WorkoutStore";
import WorkoutContentStore from "stores/WorkoutContentStore";

import { Placeholders } from "assets/images/placeholders";

import GridCollection from "components/GridCollection";
import Placeholder from "components/Placeholder";
import Spinner from "components/Spinner";
import NoInternetConnectionPlaceholder from "components/NoInternetConnectionPlaceholder";

import { NoInternetConnectionError, NotFoundError } from "services/api";

import { redirectToRootRoute } from "utils/route";

import { WEEK } from "constants/common";
import { CONTENT_STORE_TYPE } from "constants/workout";
import { withTranslation, WithTranslation } from "react-i18next";

import {
  Wrapper,
  PlaceholderWrapper,
  LoaderContainer,
  NoInternetConnectionErrorContainer,
} from "./styles";

type Props = {
  categoryId: number | null;
  workoutGroup: string | null;
  onNavigateToWorkout: (workoutGroup?: string) => (workout: any) => void;
  onNavigateToWorkoutMain: () => void;
  isFullAccessOnly?: boolean;
  fromPopup?: boolean;
  onClose?: () => void;
  url?: string;
  history?: any;
} & WithTranslation;

const Workouts: React.FC<Props> = props => {
  const {
    categoryId,
    workoutGroup,
    onNavigateToWorkoutMain,
    isFullAccessOnly = false,
    onNavigateToWorkout,
    fromPopup = false,
    onClose,
    url,
    history,
    t,
  } = props;

  const stateRef = useRef(
    observable({
      store: null as Instance<typeof WorkoutContentStore> | null,
      isNoInternetConnectionError: false,

      get workouts() {
        return this.store?.items ?? [];
      },
    }),
  );

  const state = stateRef.current;

  const isFavorites = !categoryId;

  const isWeekCategory =
    !!workoutGroup && Object.values(WEEK).includes(workoutGroup);

  const dayOfWeek = useMemo(() => {
    if (!workoutGroup) return undefined;
    return Object.keys(WEEK).find(key => WEEK[key] === workoutGroup);
  }, [workoutGroup]);

  const workoutGroupIsNewOrAll =
    !!workoutGroup && [CONTENT_STORE_TYPE.STANDARD_ALL].includes(workoutGroup);

  const placeholder = useMemo(() => {
    return (
      <PlaceholderWrapper>
        <Placeholder
          imageUrl={Placeholders.Workout}
          text={(t as (key: string) => string)("messages.no-workouts-favorites")}
          inline={false}
        />
      </PlaceholderWrapper>
    );
  }, [t]);

  const loadContent = useCallback(async () => {
    if (isFavorites) {
      if (state.store?.type === CONTENT_STORE_TYPE.STANDARD_FAVORITES) return;

      state.store = WorkoutContentStore.create({
        type: CONTENT_STORE_TYPE.STANDARD_FAVORITES,
      });
    }

    if (categoryId) {
      try {
        await WorkoutStore.fetchDetails(categoryId, isFullAccessOnly);
      } catch (error) {
        if (error instanceof NoInternetConnectionError) {
          state.isNoInternetConnectionError = true;
        }
        if (error instanceof NotFoundError && url && history) {
          redirectToRootRoute(history, url);
        }
      }
    }

    if (isFavorites) {
      state.store = WorkoutContentStore.create({
        type: CONTENT_STORE_TYPE.STANDARD_FAVORITES,
      });
    } else if (isWeekCategory && dayOfWeek !== undefined) {
      state.store = WorkoutContentStore.create({
        type: CONTENT_STORE_TYPE.WEEK,
        dayOfWeek: Number.parseInt(dayOfWeek) + 1,
        categoryId: categoryId || undefined,
      });
    } else {
      if (!workoutGroupIsNewOrAll) {
        onNavigateToWorkoutMain();
        return;
      }

      state.store = WorkoutContentStore.create({
        type: workoutGroup || undefined,
        categoryId: categoryId || undefined,
      });
    }
  }, [
    categoryId,
    isFavorites,
    isFullAccessOnly,
    isWeekCategory,
    dayOfWeek,
    workoutGroupIsNewOrAll,
    workoutGroup,
    onNavigateToWorkoutMain,
    url,
    history,
    state,
  ]);

  const didMountRef = useRef(false);

  useEffect(() => {
    const sameType =
      isFavorites &&
      state.store?.type === CONTENT_STORE_TYPE.STANDARD_FAVORITES;

    if (!didMountRef.current) {
      didMountRef.current = true;
      loadContent();
      return;
    }

    if (!sameType) {
      loadContent();
    }
  }, [categoryId, workoutGroup, isFavorites, loadContent, state.store?.type]);

  const onLoadMore = useCallback(async () => {
    if (!state.store) return;

    if (state.store.needMore && state.store.context) {
      state.store.setPage(state.store.context.page + 1);
      await state.store.fetch(state.store.context.page, isFullAccessOnly);
    }
  }, [isFullAccessOnly, state]);

  if (
    state.store?.isNoInternetConnectionError ||
    state.isNoInternetConnectionError
  ) {
    return (
      <NoInternetConnectionErrorContainer>
        <NoInternetConnectionPlaceholder
          type={fromPopup ? "popup" : "element"}
          onSubmit={onClose}
        />
      </NoInternetConnectionErrorContainer>
    );
  }

  if (state.store?.isNotFoundError && url && history) {
    redirectToRootRoute(history, url);
  }

  if (state.store?.isLoading) {
    return (
      <LoaderContainer>
        <Spinner large />
      </LoaderContainer>
    );
  }

  return (
    <Wrapper>
      {isFavorites && !state.workouts.length ? (
        placeholder
      ) : (
        <GridCollection
          items={state.workouts}
          onClick={onNavigateToWorkout()}
          needMore={state.store?.needMore}
          onLoadMore={onLoadMore}
          isWorkout
          withInfinityScroll
        />
      )}
    </Wrapper>
  );
};

export default withTranslation()(observer(Workouts));
